import { useState, useCallback, useEffect, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import { Modal, Button, Select, Input, Badge, Spinner, cn } from '@/ui'
import styles from './DiscoverModelModal.module.css'

interface DiscoverModelModalProps {
  open: boolean
  onClose: () => void
  onRegistered: () => void
}

interface DiscoveredModel {
  provider: string
  providerModel: string
  displayName: string
  modelType: string
  methods: string[]
  createdAt: string | null
  registeredVariants: { modelId: string; name: string }[]
}

interface ProviderStatus {
  provider: string
  label: string
  status: 'ok' | 'error' | 'unconfigured'
  error?: string
  modelCount: number
}

interface DiscoverResponse {
  providers: ProviderStatus[]
  models: DiscoveredModel[]
}

type SortKey = 'provider' | 'modelType' | 'displayName' | 'providerModel' | 'createdAt' | 'status'
type SortDir = 'asc' | 'desc'

const PROVIDER_LABELS: Record<string, string> = {
  gemini: 'Gemini',
  openai: 'OpenAI',
  xai: 'xAI',
}

const PROVIDER_COLORS: Record<string, 'blue' | 'green' | 'yellow' | 'gray' | 'red'> = {
  gemini: 'blue',
  openai: 'green',
  xai: 'yellow',
}

const MODEL_TYPE_LABELS: Record<string, string> = {
  image: '이미지',
  text: '텍스트',
  audio: '오디오',
  embedding: '임베딩',
  other: '기타',
}

const MODEL_TYPE_COLORS: Record<string, 'blue' | 'green' | 'yellow' | 'gray' | 'red'> = {
  image: 'blue',
  text: 'green',
  audio: 'yellow',
  embedding: 'gray',
  other: 'gray',
}

const FACE_OPTIONS = [
  { value: 'excellent', label: 'excellent' },
  { value: 'good', label: 'good' },
  { value: 'fair', label: 'fair' },
  { value: 'poor', label: 'poor' },
]

const SPEED_OPTIONS = [
  { value: 'fast', label: 'fast' },
  { value: 'medium', label: 'medium' },
  { value: 'slow', label: 'slow' },
]

const ASPECT_RATIO_OPTIONS = [
  { value: '', label: '기본값 (미지정)' },
  { value: '1:1', label: '1:1' },
  { value: '3:4', label: '3:4' },
  { value: '4:3', label: '4:3' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '2:3', label: '2:3' },
  { value: '3:2', label: '3:2' },
]

const IMAGE_SIZE_OPTIONS = [
  { value: '', label: '기본값 (미지정)' },
  { value: '512', label: '512' },
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
]

interface RegisterForm {
  modelId: string
  name: string
  providerType: string
  providerModel: string
  costPerImage: string
  facePreservation: string
  speed: string
  supportsReferenceImage: boolean
  aspectRatio: string
  imageSize: string
  description: string
}

function makeDefaultForm(model: DiscoveredModel): RegisterForm {
  const variantCount = model.registeredVariants.length
  return {
    modelId: variantCount > 0 ? `${model.providerModel}-v${variantCount + 1}` : model.providerModel,
    name: variantCount > 0 ? `${model.displayName} (변형 ${variantCount + 1})` : model.displayName,
    providerType: model.provider,
    providerModel: model.providerModel,
    costPerImage: '0.02',
    facePreservation: 'good',
    speed: 'medium',
    supportsReferenceImage: true,
    aspectRatio: '',
    imageSize: '',
    description: '',
  }
}

export function DiscoverModelModal({ open, onClose, onRegistered }: DiscoverModelModalProps) {
  const { addToast } = useToast()

  const [models, setModels] = useState<DiscoveredModel[]>([])
  const [providerStatuses, setProviderStatuses] = useState<ProviderStatus[]>([])
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [discoverError, setDiscoverError] = useState<string | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('provider')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [showErrors, setShowErrors] = useState(false)

  const selectProvider = useCallback((provider: string | null) => {
    setSelectedProvider(provider)
    // 특정 프로바이더 탭에서는 provider 정렬 무의미 → 리셋
    if (provider && sortKey === 'provider') {
      setSortKey('displayName')
      setSortDir('asc')
    }
  }, [sortKey])

  // 등록 폼 상태
  const [form, setForm] = useState<RegisterForm | null>(null)
  const [isRegistering, setIsRegistering] = useState(false)

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortDir('asc')
      return key
    })
  }, [])

  const filteredModels = useMemo(() => {
    let list = models

    // 프로바이더 필터
    if (selectedProvider) {
      list = list.filter((m) => m.provider === selectedProvider)
    }

    // 검색 필터
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (m) => m.displayName.toLowerCase().includes(q) || m.providerModel.toLowerCase().includes(q),
      )
    }

    // 정렬
    const dir = sortDir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      if (sortKey === 'status') {
        return (a.registeredVariants.length - b.registeredVariants.length) * dir
      }
      if (sortKey === 'createdAt') {
        const va = a.createdAt ?? ''
        const vb = b.createdAt ?? ''
        return va.localeCompare(vb) * dir
      }
      const va = (a[sortKey] ?? '').toLowerCase()
      const vb = (b[sortKey] ?? '').toLowerCase()
      return va.localeCompare(vb) * dir
    })
  }, [models, selectedProvider, searchQuery, sortKey, sortDir])

  const handleDiscover = useCallback(async () => {
    setIsDiscovering(true)
    setDiscoverError(null)
    setShowErrors(true)
    setForm(null)
    try {
      const data = await apiFetch<DiscoverResponse>('/api/admin/ai-models/discover')
      setModels(data.models)
      setProviderStatuses(data.providers)
    } catch (e: any) {
      setDiscoverError(e.message)
      setModels([])
      setProviderStatuses([])
    } finally {
      setIsDiscovering(false)
    }
  }, [])

  // 모달 열릴 때 자동 검색
  useEffect(() => {
    if (open) {
      setSelectedProvider(null)
      setSearchQuery('')
      setSortKey('provider')
      setSortDir('asc')
      handleDiscover()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartRegister = useCallback((model: DiscoveredModel) => {
    setForm(makeDefaultForm(model))
  }, [])

  const handleCancelRegister = useCallback(() => {
    setForm(null)
  }, [])

  const handleRegister = useCallback(async () => {
    if (!form) return
    setIsRegistering(true)
    try {
      await apiFetch('/api/admin/ai-models/register', {
        method: 'POST',
        body: JSON.stringify({
          modelId: form.modelId,
          name: form.name,
          providerType: form.providerType,
          providerModel: form.providerModel,
          costPerImage: parseFloat(form.costPerImage),
          facePreservation: form.facePreservation,
          speed: form.speed,
          supportsReferenceImage: form.supportsReferenceImage,
          aspectRatio: form.aspectRatio || null,
          imageSize: form.imageSize || null,
          description: form.description,
        }),
      })
      addToast({ type: 'success', message: `${form.name} 모델이 등록되었습니다.` })
      onRegistered()
    } catch (e: any) {
      addToast({ type: 'error', message: e.message })
    } finally {
      setIsRegistering(false)
    }
  }, [form, addToast, onRegistered])

  const updateForm = useCallback((field: keyof RegisterForm, value: string | boolean) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev))
  }, [])

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return ''
    return sortDir === 'asc' ? ' ▲' : ' ▼'
  }

  const providerErrors = providerStatuses.filter((ps) => ps.status === 'error')

  return (
    <Modal open={open} onClose={onClose} title="모델 검색" size="lg">
      <div className={styles.root}>
        {/* 프로바이더 필터 탭 */}
        {isDiscovering ? (
          <div className={styles.discoverStatus}>
            <Spinner size="sm" /> 모델 검색 중...
          </div>
        ) : (
          <div className={styles.providerBar}>
            <button
              type="button"
              className={cn(styles.filterTab, selectedProvider === null && styles.filterTabActive)}
              onClick={() => selectProvider(null)}
            >
              전체 ({models.length})
            </button>
            {providerStatuses.map((ps) => (
              <button
                type="button"
                key={ps.provider}
                className={cn(styles.filterTab, selectedProvider === ps.provider && styles.filterTabActive)}
                disabled={ps.status !== 'ok'}
                onClick={() => selectProvider(ps.provider)}
              >
                <Badge
                  color={
                    ps.status === 'ok'
                      ? PROVIDER_COLORS[ps.provider] ?? 'green'
                      : ps.status === 'error'
                        ? 'red'
                        : 'gray'
                  }
                >
                  {ps.label}
                  {ps.status === 'ok' && ` (${ps.modelCount})`}
                  {ps.status === 'error' && ' (오류)'}
                  {ps.status === 'unconfigured' && ' (미설정)'}
                </Badge>
              </button>
            ))}
            <Button size="sm" variant="ghost" onClick={handleDiscover}>
              새로고침
            </Button>
          </div>
        )}

        {discoverError && <div className={styles.error}>{discoverError}</div>}

        {/* 프로바이더별 에러 — 새로고침 직후만 표시 */}
        {showErrors && providerErrors.length > 0 && (
          <div className={styles.errorBlock}>
            {providerErrors.map((ps) => (
              <div key={ps.provider} className={styles.error}>
                {ps.label}: {ps.error}
              </div>
            ))}
            <button type="button" className={styles.errorDismiss} onClick={() => setShowErrors(false)}>
              닫기
            </button>
          </div>
        )}

        {/* 검색 */}
        {models.length > 0 && (
          <Input
            size="sm"
            placeholder="모델명 또는 Provider Model로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}

        {/* 검색 결과 테이블 */}
        {filteredModels.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {!selectedProvider && (
                    <th className={styles.sortable} onClick={() => handleSort('provider')}>
                      프로바이더{sortIndicator('provider')}
                    </th>
                  )}
                  <th className={styles.sortable} onClick={() => handleSort('modelType')}>
                    타입{sortIndicator('modelType')}
                  </th>
                  <th className={styles.sortable} onClick={() => handleSort('displayName')}>
                    모델명{sortIndicator('displayName')}
                  </th>
                  <th className={styles.sortable} onClick={() => handleSort('providerModel')}>
                    Provider Model{sortIndicator('providerModel')}
                  </th>
                  <th className={styles.sortable} onClick={() => handleSort('createdAt')}>
                    출시일{sortIndicator('createdAt')}
                  </th>
                  <th className={styles.sortable} onClick={() => handleSort('status')}>
                    상태{sortIndicator('status')}
                  </th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredModels.map((model) => (
                  <tr key={`${model.provider}-${model.providerModel}`}>
                    {!selectedProvider && (
                      <td>
                        <Badge color={PROVIDER_COLORS[model.provider] ?? 'gray'}>
                          {PROVIDER_LABELS[model.provider] ?? model.provider}
                        </Badge>
                      </td>
                    )}
                    <td>
                      <Badge color={MODEL_TYPE_COLORS[model.modelType] ?? 'gray'}>
                        {MODEL_TYPE_LABELS[model.modelType] ?? model.modelType}
                      </Badge>
                    </td>
                    <td>{model.displayName}</td>
                    <td className={styles.mono}>{model.providerModel}</td>
                    <td className={styles.mono}>{model.createdAt ?? '-'}</td>
                    <td>
                      {model.registeredVariants.length > 0 ? (
                        <Badge color="green">{model.registeredVariants.length}개 등록</Badge>
                      ) : (
                        <Badge color="gray">미등록</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartRegister(model)}
                        disabled={form?.providerModel === model.providerModel}
                      >
                        {model.registeredVariants.length > 0 ? '변형 추가' : '등록'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isDiscovering && models.length > 0 && filteredModels.length === 0 && (
          <div className={styles.empty}>검색 결과가 없습니다.</div>
        )}

        {/* 인라인 등록 폼 */}
        {form && (
          <div className={styles.registerForm}>
            <div className={styles.registerHeader}>
              <strong>{form.name} 등록</strong>
              <Button size="sm" variant="ghost" onClick={handleCancelRegister}>
                취소
              </Button>
            </div>

            <div className={styles.formGrid}>
              <Input
                size="sm"
                label="Model ID"
                value={form.modelId}
                onChange={(e) => updateForm('modelId', e.target.value)}
              />
              <Input
                size="sm"
                label="이름"
                value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
              />
              <Input
                size="sm"
                label="비용/이미지 ($)"
                type="number"
                step="0.01"
                value={form.costPerImage}
                onChange={(e) => updateForm('costPerImage', e.target.value)}
              />
              <Select
                size="sm"
                label="얼굴 보존"
                value={form.facePreservation}
                onChange={(e) => updateForm('facePreservation', e.target.value)}
                options={FACE_OPTIONS}
              />
              <Select
                size="sm"
                label="속도"
                value={form.speed}
                onChange={(e) => updateForm('speed', e.target.value)}
                options={SPEED_OPTIONS}
              />
              <Select
                size="sm"
                label="비율 (aspectRatio)"
                value={form.aspectRatio}
                onChange={(e) => updateForm('aspectRatio', e.target.value)}
                options={ASPECT_RATIO_OPTIONS}
              />
              <Select
                size="sm"
                label="해상도 (imageSize)"
                value={form.imageSize}
                onChange={(e) => updateForm('imageSize', e.target.value)}
                options={IMAGE_SIZE_OPTIONS}
              />
              <div className={styles.checkboxField}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.supportsReferenceImage}
                    onChange={(e) => updateForm('supportsReferenceImage', e.target.checked)}
                  />
                  참조 이미지 지원
                </label>
              </div>
            </div>

            <Input
              size="sm"
              label="설명"
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              placeholder="모델 설명 (선택)"
            />

            <div className={styles.registerActions}>
              <Button size="sm" variant="primary" onClick={handleRegister} loading={isRegistering}>
                등록하기
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
